import { useMutation } from '@apollo/client';
import React, { useCallback, useMemo } from 'react';

import { Stack, Text } from '@eluve/components';
import {
  DynamicArtifactRenderer,
  DynamicArtifactSaveContextProps,
} from '@eluve/smart-blocks';
import { convertArrayToPgLiteral } from '@eluve/utils';

import { useGoldenSummaryContext } from './GoldenSummaryContextProvider';
import { updateGoldenSummaryMutation } from './operations';
import { useGoldenSummaries } from './useGoldenSummaries';

interface GoldenSummaryEditorProps {
  tenantId: string;
  appointmentId: string;
  activeNoteId: string;
}

export const GoldenSummaryEditor: React.FC<GoldenSummaryEditorProps> = ({
  tenantId,
  appointmentId,
  activeNoteId,
}) => {
  const { goldenNote } = useGoldenSummaries(
    tenantId,
    appointmentId,
    activeNoteId,
  );
  const { setHasPendingChanges } = useGoldenSummaryContext();
  const [updateGoldenSummary] = useMutation(updateGoldenSummaryMutation);

  const saveBlock: DynamicArtifactSaveContextProps['saveBlock'] | undefined =
    useMemo(() => {
      if (!goldenNote || goldenNote?.status === 'COMPLETED') {
        return undefined;
      }

      return async (blockPath, value) => {
        if (!blockPath || value === undefined) {
          return;
        }

        await updateGoldenSummary({
          variables: {
            id: activeNoteId,
            path: convertArrayToPgLiteral(blockPath),
            value: value as Record<string, unknown>,
          },
          onCompleted: () => setHasPendingChanges(false),
        });
      };
    }, [goldenNote, updateGoldenSummary, activeNoteId, setHasPendingChanges]);

  const onPendingChanges: DynamicArtifactSaveContextProps['onPendingChanges'] =
    useCallback(() => setHasPendingChanges(true), [setHasPendingChanges]);

  if (!goldenNote) {
    return (
      <Stack
        gap={4}
        className="py-8"
        justify="center"
        fullHeight
        align="center"
      >
        <Text>Fetching selected golden note...</Text>
      </Stack>
    );
  }

  if (goldenNote.status === 'COMPLETED') {
    return (
      <>
        <Text className="w-full rounded-lg bg-bg-tertiary p-4 text-center">
          This golden summary has been finalized and cannot be edited.
        </Text>
        {goldenNote.humanSummary && (
          <DynamicArtifactRenderer
            isReadonly={true}
            blocks={goldenNote.humanSummary.blocks}
          />
        )}
      </>
    );
  }

  return goldenNote.humanSummary ? (
    <DynamicArtifactRenderer
      isReadonly={false}
      blocks={goldenNote.humanSummary.blocks}
      saveBlock={saveBlock}
      onPendingChanges={onPendingChanges}
    />
  ) : (
    <Stack gap={4} className="py-8" justify="center" fullHeight align="center">
      <Text>No content available for this golden summary.</Text>
    </Stack>
  );
};
